document.documentElement.classList.remove('no-js');

(() => {
  const qs = (selector, root = document) => root.querySelector(selector);
  const qsa = (selector, root = document) => [...root.querySelectorAll(selector)];

  function initMobileMenu() {
    const menu = qs('[data-mobile-menu]');
    const open = qs('[data-menu-open]');
    if (!menu || !open) return;
    const closeButtons = qsa('[data-menu-close]', menu);

    const setOpen = (isOpen) => {
      menu.setAttribute('aria-hidden', isOpen ? 'false' : 'true');
      open.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
      document.body.classList.toggle('menu-open', isOpen);
      if (isOpen) qs('a, button', menu)?.focus();
    };

    open.addEventListener('click', () => setOpen(true));
    closeButtons.forEach((button) => button.addEventListener('click', () => setOpen(false)));
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && menu.getAttribute('aria-hidden') === 'false') setOpen(false);
    });
  }

  function initCarousels() {
    qsa('[data-carousel]').forEach((carousel) => {
      const track = qs('[data-carousel-track]', carousel);
      if (!track) return;
      const prev = qs('[data-carousel-prev]', carousel);
      const next = qs('[data-carousel-next]', carousel);

      const amount = () => {
        const first = track.firstElementChild;
        if (!first) return 360;
        return first.getBoundingClientRect().width + 16;
      };
      prev?.addEventListener('click', () => track.scrollBy({ left: -amount(), behavior: 'smooth' }));
      next?.addEventListener('click', () => track.scrollBy({ left: amount(), behavior: 'smooth' }));
    });
  }

  function initProductGalleries(root = document) {
    qsa('[data-product-gallery]', root).forEach((gallery) => {
      if (gallery.dataset.galleryInitialized === 'true') return;

      gallery.addEventListener('click', (event) => {
        const thumb = event.target.closest('[data-gallery-thumb]');
        if (!thumb || !gallery.contains(thumb)) return;

        const main = qs('[data-gallery-main]', gallery);
        const src = thumb.dataset.src;
        const srcset = thumb.dataset.srcset || '';
        const alt = thumb.dataset.alt || '';
        if (!main || main.tagName !== 'IMG' || !src) return;

        event.preventDefault();

        // Shopify product images use srcset. Updating only src can leave the
        // old responsive image visible, so update both attributes together.
        if (srcset) {
          main.setAttribute('srcset', srcset);
        } else {
          main.removeAttribute('srcset');
        }
        main.setAttribute('src', src);
        main.setAttribute('alt', alt);

        qsa('[data-gallery-thumb]', gallery).forEach((item) => {
          item.setAttribute('aria-current', 'false');
        });
        thumb.setAttribute('aria-current', 'true');
      });

      gallery.dataset.galleryInitialized = 'true';
    });
  }

  function formatMoney(cents, currency = 'USD') {
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en-US', {
        style: 'currency',
        currency
      }).format(cents / 100);
    } catch (_) {
      return `$${(cents / 100).toFixed(2)}`;
    }
  }

  function initProductForms() {
    qsa('[data-product-form-wrap]').forEach((wrap) => {
      const select = qs('[data-variant-select]', wrap);
      const price = qs('[data-product-price]', wrap);
      const compare = qs('[data-product-compare]', wrap);
      const button = qs('[data-add-to-cart]', wrap);
      const buttonLabel = qs('[data-add-to-cart-label]', wrap);
      const stickyPrice = qs('[data-sticky-price]');
      const variantsNode = qs('[data-variants-json]', wrap);
      if (!select || !variantsNode) return;

      let variants = [];
      try { variants = JSON.parse(variantsNode.textContent); } catch (_) { return; }
      const currency = wrap.dataset.currency || 'USD';

      const update = () => {
        const id = String(select.value);
        const variant = variants.find((item) => String(item.id) === id);
        if (!variant) return;

        if (price) price.textContent = formatMoney(variant.price, currency);
        if (stickyPrice) stickyPrice.textContent = formatMoney(variant.price, currency);

        if (compare) {
          if (variant.compare_at_price && variant.compare_at_price > variant.price) {
            compare.hidden = false;
            compare.textContent = formatMoney(variant.compare_at_price, currency);
          } else {
            compare.hidden = true;
          }
        }

        if (button) {
          button.disabled = !variant.available;
          if (buttonLabel) buttonLabel.textContent = variant.available ? 'Add to cart' : 'Sold out';
        }

        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url);
      };

      select.addEventListener('change', update);
      update();
    });
  }

  function initStickyAddToCart() {
    const sticky = qs('[data-sticky-atc]');
    const mainButton = qs('[data-main-atc]');
    if (!sticky || !mainButton || !('IntersectionObserver' in window)) return;
    const observer = new IntersectionObserver((entries) => {
      sticky.classList.toggle('is-visible', !entries[0].isIntersecting);
    }, { threshold: 0 });
    observer.observe(mainButton);

    qs('[data-sticky-atc-button]', sticky)?.addEventListener('click', () => mainButton.click());
  }

  function initQuiz() {
    const quiz = qs('[data-quiz]');
    if (!quiz) return;

    const steps = qsa('[data-quiz-step]', quiz);
    const progress = qs('[data-quiz-progress]', quiz);
    const resultStep = qs('[data-quiz-result]', quiz);
    const title = qs('[data-result-title]', quiz);
    const copy = qs('[data-result-copy]', quiz);
    const tagField = qs('[data-result-tag]', quiz);
    let index = 0;
    const answers = {};

    const show = (nextIndex) => {
      index = Math.max(0, Math.min(nextIndex, steps.length - 1));
      steps.forEach((step, stepIndex) => { step.hidden = stepIndex !== index; });
      if (progress) progress.style.width = `${((index + 1) / steps.length) * 100}%`;
      const back = qs('[data-quiz-back]', steps[index]);
      if (back) back.hidden = index === 0;
    };

    qsa('[data-quiz-option]', quiz).forEach((option) => {
      option.addEventListener('click', () => {
        const step = option.closest('[data-quiz-step]');
        const key = step?.dataset.key;
        const value = option.dataset.value;
        if (!key || !value) return;
        answers[key] = value;
        qsa('[data-quiz-option]', step).forEach((item) => item.classList.remove('is-selected'));
        option.classList.add('is-selected');
        qs('[data-quiz-next]', step)?.removeAttribute('disabled');
        qs('[data-quiz-submit]', step)?.removeAttribute('disabled');
      });
    });

    qsa('[data-quiz-next]', quiz).forEach((button) => {
      button.addEventListener('click', () => {
        if (index < steps.length - 1) show(index + 1);
      });
    });
    qsa('[data-quiz-back]', quiz).forEach((button) => {
      button.addEventListener('click', () => show(index - 1));
    });

    const calculate = () => {
      const scores = { fog: 0, energy: 0, focus: 0, stress: 0 };
      Object.values(answers).forEach((value) => {
        if (Object.prototype.hasOwnProperty.call(scores, value)) scores[value] += 1;
      });
      const result = Object.entries(scores).sort((a, b) => b[1] - a[1])[0][0];
      const content = {
        fog: {
          title: 'Your pattern: The Foggy Starter',
          copy: 'Your biggest opportunity is creating a consistent morning rhythm that supports mental clarity before distractions stack up.'
        },
        energy: {
          title: 'Your pattern: The Afternoon Fader',
          copy: 'Your energy is doing the driving. A steadier daily routine may help you avoid chasing another drink when the afternoon dip hits.'
        },
        focus: {
          title: 'Your pattern: The Tab Hopper',
          copy: 'You have plenty to do, but locking in is the challenge. Your best next step is a simple focus ritual you can repeat every day.'
        },
        stress: {
          title: 'Your pattern: The Overloaded Achiever',
          copy: 'You push hard, but mental overload makes consistency difficult. A calmer, simpler routine can help you protect your attention.'
        }
      }[result];

      if (title) title.textContent = content.title;
      if (copy) copy.textContent = content.copy;
      if (tagField) tagField.value = `newsletter,prelaunch,focus-quiz,quiz-${result}`;
      steps.forEach((step) => { step.hidden = true; });
      if (resultStep) resultStep.hidden = false;
      if (progress) progress.style.width = '100%';
      resultStep?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    };

    qs('[data-quiz-submit]', quiz)?.addEventListener('click', calculate);
    show(0);
  }

  function initQuantityUpdates() {
    qsa('[data-cart-quantity]').forEach((input) => {
      input.addEventListener('change', () => {
        const form = input.closest('form');
        form?.submit();
      });
    });
  }

  document.addEventListener('DOMContentLoaded', () => {
    initMobileMenu();
    initCarousels();
    initProductGalleries();
    initProductForms();
    initStickyAddToCart();
    initQuiz();
    initQuantityUpdates();
  });

  document.addEventListener('shopify:section:load', (event) => {
    initProductGalleries(event.target);
  });
})();
